import * as turf from "@turf/turf";
import {Feature, FeatureCollection, LineString, Point, Polygon, Position} from "geojson";
import {BBox} from "@turf/turf";
//@ts-ignore
import geodesic from 'geographiclib-geodesic';
import {getArrayDepth} from '../../js/utils'
import proj4 from 'proj4';

export class GeometryUtil {
  static lineString(points: Position[]): Feature<LineString> {
    return turf.lineString(points);
  }

  static multiLineString(points: Position[][]): Feature<turf.MultiLineString> {
    return turf.multiLineString(points);
  }

  static polygon(points: Position[]): Feature<Polygon> {
    return turf.polygon([points]);
  }

  static circle(points: Position[]): Feature<Polygon> {
    return turf.polygon([points]);
  }

  static point(point: Position): Feature<Point> {
    return turf.point(point);
  }

  static forEachPosition(feature: Feature, callback: (currentCoord: Position, coordIndex: number, geometryIndex: number, multiFeatureIndex: number) => void) {
    switch (feature.geometry.type) {
      case 'Polygon':
        feature.geometry.coordinates.forEach((ring, ringIndex) => {
          ring.forEach((coord, index) => callback(coord, index, ringIndex, 0))
        });
        break;
      case 'MultiPolygon':
        feature.geometry.coordinates.forEach((feature, featureIndex) => {
          feature.forEach((ring, ringIndex) => {
            ring.map((coord, index) => callback(coord, index, ringIndex, featureIndex))
          });
        });
        break;
      case 'LineString':
        feature.geometry.coordinates.forEach((coord, index) => callback(coord, index, 0, 0));
        break;
      case 'MultiLineString':
        feature.geometry.coordinates.forEach((feature, featureIndex) => {
          feature.forEach((coord, index) => callback(coord, index, 0, featureIndex));
        })
        break;
      case 'Point':
        callback(feature.geometry.coordinates, 0, 0, 0)
    }
  }

  static intersect(firstFeature: Feature, secondFeature: Feature): Feature {
    return turf.intersect(firstFeature, secondFeature);
  }

  static bboxPolygon(bbox: BBox) {
    return turf.bboxPolygon(bbox);
  }

  static bbox(feature: Feature | FeatureCollection): [number, number, number, number] {
    return turf.bbox(feature) as any;
  }

  static center(feature: Feature) {
    return turf.center(feature as any).geometry.coordinates;
  }

  static centroid(polygon: any) {
    return turf.centroid(polygon);
  }

  static area(feature: Feature | any): number {
    return parseInt(turf.area(feature) as any);
    //return parseInt(turf.area(GeometryUtil.projectFeature(feature,"EPSG:4326","EPSG:15497")) as any);
  }

  static projectCoordinates(coordinates, from, to) {
    proj4.defs('EPSG:15497', '+proj=sterea +lat_0=46 +lon_0=25 +k=0.99975 +x_0=500000 +y_0=500000 +ellps=krass +towgs84=28,-121,-77,0,0,0,0 +units=m +no_defs');
    proj4.defs("EPSG:3395", "+proj=merc +lon_0=0 +k=1 +x_0=0 +y_0=0 +datum=WGS84 +units=m +no_defs");
    return proj4(from, to, coordinates);
  }

  static projectFeature(feature: Feature | any, from: any, to: any): Feature | any {
    if (turf.getType(feature) === 'Point') {
      feature.geometry.coordinates = GeometryUtil.projectCoordinates(feature.geometry.coordinates, from, to);
    } else if (turf.getType(feature) === 'Polygon' || turf.getType(feature) === 'MultiPolygon') {
      feature.geometry.coordinates = feature.geometry.coordinates.map((ring: any) => {
        return ring.map((coord: any) => GeometryUtil.projectCoordinates(coord, from, to));
      });
    } else if (turf.getType(feature) === 'MultiPoint' || turf.getType(feature) === 'LineString' || turf.getType(feature) === 'MultiLineString') {
      feature.geometry.coordinates = feature.geometry.coordinates.map((coord: any) => GeometryUtil.projectCoordinates(coord, from, to));
    }

    return feature;
  }

  static projectWgsToStereo70(coordinates) {
    proj4.defs('EPSG:4326', '+proj=longlat +datum=WGS84 +no_defs');
    proj4.defs('EPSG:15497', '+proj=sterea +lat_0=46 +lon_0=25 +k=0.99975 +x_0=500000 +y_0=500000 +ellps=krass +towgs84=28,-121,-77,0,0,0,0 +units=m +no_defs');
    const wgs84Coordinates = coordinates; // Longitude, Latitude (in degrees)
    const stereo70Coordinates = proj4('EPSG:4326', 'EPSG:15497', wgs84Coordinates);
    return stereo70Coordinates;
  }
  static projectStereo70ToWgs(coordinates) {
    proj4.defs('EPSG:4326', '+proj=longlat +datum=WGS84 +no_defs');
    proj4.defs('EPSG:15497', '+proj=sterea +lat_0=46 +lon_0=25 +k=0.99975 +x_0=500000 +y_0=500000 +ellps=krass +towgs84=28,-121,-77,0,0,0,0 +units=m +no_defs');
    const stereo70Coordinates = coordinates; // Longitude, Latitude (in degrees)
    const wgs84Coordinates = proj4('EPSG:15497','EPSG:4326', stereo70Coordinates);
    return wgs84Coordinates;
  }

  static centerMedian(feature: Feature): Position {
    return turf.centerMedian(feature as any).geometry.coordinates;
  }

  static centerOfMass(feature: Feature): Position {
    return turf.centerOfMass(feature as any).geometry.coordinates;
  }

  static alongLine(feature: Feature<LineString>, distance: number): Position {
    return turf.along(feature, distance, {units: 'meters'}).geometry.coordinates;
  }

  static totalLength(feature: Feature | any): number {
    //return turf.length(feature, { units: 'meters' });
    return this.calculateLength(feature.geometry.coordinates)
  }

  static midpoint(start: Position, end: Position): Position {
    return turf.midpoint(start, end).geometry.coordinates;
  }

  static pointsBearing(pointA: Position, pointB: Position): number {
    let result =geodesic.Geodesic.WGS84.Inverse(pointA[1], pointA[0], pointB[1], pointB[0]);
    return result.azi1;
  }

  static pointsDistance(pointA: Position, pointB: Position): number {
    //return turf.rhumbDistance(pointA, pointB, { units: 'meters' });
    return this.calculateDistance(pointA, pointB)
  }

  static pointToLineDistance(point: Position, line: Position[]) {
    //@ts-ignore
    return turf.pointToLineDistance(point, line, {units: 'meters'})
  }

  static pointsTranslate(point: Position, distance, bearing): Position {
    let translated = geodesic.Geodesic.WGS84.Direct(point[1], point[0], bearing, distance);
    return [translated.lon2, translated.lat2];
  }

  static scale(feature: Feature, scale: number, origin: Position): Feature {
    return turf.transformScale(feature as any, scale, {origin});
  }

  static translate(feature: Feature, distance: number, bearing: number): Feature {
    return turf.transformTranslate(feature as any, distance, bearing, {units: 'meters'});
  }

  static rotate(feature: Feature, rotation: number, pivot: Position): Feature {
    return turf.transformRotate(feature as any, rotation, {pivot: pivot});
  }

  static lineIntersect(featureA, featureB) {
    return turf.lineIntersect(featureA, featureB);
  }

  static pointsAngle(pointA: Position, pointB: Position, pointC: Position, correction: boolean = true) {
    let angle = turf.angle(pointA, pointB, pointC, {mercator: true});
    if (angle > 180) {
      angle = 360 - angle;
    }
    return angle;
  }

  static createBBoxFromPoint(x: number, y: number, threshold: number) {
    return [[x - threshold, y - threshold], [x + threshold, y + threshold]];
  }

  static metersToPixels(distance: number, latitude: number, zoom: number): number {
    let pixelDensity = 2; //1 normal resolution, 2 for hdpi (todo: switch)
    return (distance / this.metersPerPixel(latitude, zoom)) * pixelDensity;
  }

  static metersPerPixel(latitude: number, zoom: number): number {
    let earthCircumference = 40075017;
    let latitudeRadians = latitude * (Math.PI / 180);
    return earthCircumference * Math.cos(latitudeRadians) / Math.pow(2, zoom + 8);
  }

  static contains(position: Position, polygon: Polygon): boolean {
    return turf.inside(position, polygon);
  }

  static relativePosition(position: Position, rect: Position[], size: {x: number, y: number}): {x: number, y: number} {
    let x = position[0] - rect[0][0];
    let y = position[1] - rect[0][1];

    let totalX = rect[1][0] - rect[0][0];
    let totalY = rect[2][1] - rect[1][1]

    return {x: (x / totalX) * size.x, y: (y / totalY) * size.y};
  }

  static generateRectangle(pointA: Position, pointB: Position, x: number, y: number) {
    let distance = this.pointsDistance(pointA, pointB);
    let bearing = this.pointsBearing(pointA, pointB)

    let height = (distance / x) * y;

    let pointC = this.pointsTranslate(pointB, height, bearing + 90);

    return [
      pointA,
      pointB,
      [pointB[0], pointC[1]],
      [pointA[0], pointC[1]],
    ]
  }


  public static calculateArea(coordinates) {
    let result = 0;
    let type = getArrayDepth(coordinates) === 4 ? 'MultiPolygon' : 'Polygon';
    switch (type) {
      case 'Polygon':
        result = this.polygonArea([coordinates]);
        break
      case 'MultiPolygon':
        result = coordinates.reduce((value, coords) => value + this.polygonArea([coords]), 0);
        break
      default:
        result = 0;
    }

    return result;
  }


  private static polygonArea(coords: number[][][][]) {
    let total = 0;
    if (coords && coords.length > 0) {
      for (let i = 0; i < coords.length; i++) {
        const area = this.geographicLibArea(coords[i][0]);
        total += area;
      }
    }
    return total;
  }

  private static geographicLibArea(coordinates: Array<turf.Coord>) {
    const geod = geodesic.Geodesic.WGS84;
    let poly = geod.Polygon(false);
    for (let i = 0; i < coordinates.length; ++i) {
      poly.AddPoint(coordinates[i][1], coordinates[i][0]);
    }

    poly = poly.Compute(false, true);

    return Math.abs(poly.area);
  }


  public static calculatePerimeter(coordinates) {
    let result = 0;
    let type = getArrayDepth(coordinates) === 4 ? 'MultiPolygon' : 'Polygon';
    if (coordinates?.length)
      switch (type) {
        case 'Polygon':
          result = this.polygonPerimeter([coordinates]);
          break
        case 'MultiPolygon':

          result = coordinates.reduce((value, coords) => value + this.polygonPerimeter([coords]), 0);
          break
        default:
          result = 0;
      }

    return result;
  }

  private static polygonPerimeter(coords: number[][][][]) {
    let total = 0;
    if (coords && coords.length > 0) {
      for (let i = 0; i < coords.length; i++) {
        const perimeter = this.geographicLibPerimeter(coords[i][0]);
        total += perimeter;
      }
    }
    return total;
  }

  private static geographicLibPerimeter(coordinates: Array<turf.Coord>) {
    const geod = geodesic.Geodesic.WGS84;
    let poly = geod.Polygon(false);
    for (let i = 0; i < coordinates.length; ++i) {
      poly.AddPoint(coordinates[i][1], coordinates[i][0]);
    }

    poly = poly.Compute(false, true);

    return Math.abs(poly.perimeter);
  }

  public static calculateDistance(pointA: number[], pointB: number[]) {
    const geod = geodesic.Geodesic.WGS84;
    let poly = geod.Inverse(pointA[1], pointA[0], pointB[1], pointB[0])
    if (poly && poly['s12'])
      return Math.abs(poly['s12']);
    return 0;
  }

  public static calculateLength(coordinates) {
    let result = 0;
    let type = getArrayDepth(coordinates) === 3 ? 'MultiLineString' : 'LineString';
    switch (type) {
      case 'LineString':
        result = this.lineLength(coordinates);
        break
      case 'MultiLineString':
        result = coordinates.reduce((value, coords) => value + this.lineLength(coords), 0);
        break
      default:
        result = 0;
    }
    return result;
  }


  private static lineLength(coords: number[][]) {
    let total = 0;
    if (coords && coords.length > 0) {
      for (let i = 1; i < coords.length; i++) {
        const length = this.calculateDistance(coords[i - 1], coords[i]);
        total += length;
      }
    }
    return total;
  }

  static rewind(feature, reverse: boolean | undefined): any {
    return turf.rewind(feature, {reversed: reverse});
  }
}
